import { useCallback, useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material"

export interface SaveAsNewDialogProps {
  open: boolean
  onClose: () => void
  // Resolves on success (dialog closes); rejects to keep the dialog open with the inline error.
  onSave: (name: string) => Promise<void>
}

export default function SaveAsNewDialog({ open, onClose, onSave }: SaveAsNewDialogProps) {
  const [name, setName] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = useCallback(() => {
    setName("")
    setIsSaving(false)
    setError(null)
  }, [])

  const handleClose = useCallback(() => {
    if (isSaving) return
    reset()
    onClose()
  }, [isSaving, reset, onClose])

  const trimmedName = name.trim()
  const isSaveDisabled = trimmedName.length === 0 || isSaving

  const handleSave = useCallback(async () => {
    if (trimmedName.length === 0 || isSaving) return
    setIsSaving(true)
    setError(null)
    try {
      await onSave(trimmedName)
      reset()
      onClose()
    } catch {
      // Keep the dialog open so the user can retry without re-typing the name.
      setError("Couldn't save the layout. Please try again.")
      setIsSaving(false)
    }
  }, [trimmedName, isSaving, onSave, reset, onClose])

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Save as new layout</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          fullWidth
          margin="dense"
          label="Layout name"
          value={name}
          onChange={(event) => { setName(event.target.value) }}
          error={error !== null}
          helperText={error ?? undefined}
          disabled={isSaving}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault()
              void handleSave()
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>Cancel</Button>
        <Button onClick={() => { void handleSave() }} disabled={isSaveDisabled} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
